import { useMemo, useRef, useState } from "react";
import {
  Upload,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  Plus,
  GitCompareArrows,
  Layers,
  Sparkles,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { generateGroupCaption, renameGroup, deleteGroup } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_GROUP = "Main";

const normalize = (raw) => {
  if (typeof raw === "string")
    return { url: raw, kind: "before", group: DEFAULT_GROUP, at: null, by: null };
  return { kind: "before", group: DEFAULT_GROUP, ...(raw || {}) };
};

export default function PhotosPanel({ photos = [], canEdit, uploading, onUpload, woId, captions = {}, onChanged, aiEnabled = true }) {
  const items = useMemo(() => photos.map(normalize), [photos]);
  const [extraGroups, setExtraGroups] = useState([]);
  const [compare, setCompare] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");

  // Build group list — preserve insertion order from existing photos, then extras
  const groups = useMemo(() => {
    const seen = new Set();
    const list = [];
    items.forEach((p) => {
      const g = p.group || DEFAULT_GROUP;
      if (!seen.has(g)) {
        seen.add(g);
        list.push(g);
      }
    });
    if (!seen.has(DEFAULT_GROUP)) {
      list.unshift(DEFAULT_GROUP);
      seen.add(DEFAULT_GROUP);
    }
    extraGroups.forEach((g) => {
      if (!seen.has(g)) {
        seen.add(g);
        list.push(g);
      }
    });
    return list;
  }, [items, extraGroups]);

  const photosFor = (g) => items.filter((p) => (p.group || DEFAULT_GROUP) === g);

  const handleAddGroup = () => {
    const name = newName.trim();
    if (!name) return;
    if (groups.includes(name)) {
      setAdding(false);
      setNewName("");
      return;
    }
    setExtraGroups([...extraGroups, name]);
    setAdding(false);
    setNewName("");
  };

  return (
    <div className="engineered-card p-5" data-testid="wo-photos-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
            Photos · Evidence
          </div>
          <div className="font-display font-bold text-lg text-zinc-950 mt-0.5 flex items-center gap-2">
            <Layers className="h-4 w-4 text-orange-500" />
            Before vs. After
            <span className="text-xs font-mono text-zinc-500 font-normal">
              · {groups.length} {groups.length === 1 ? "group" : "groups"}
            </span>
          </div>
        </div>
        {canEdit && !adding && (
          <button
            data-testid="add-issue-group-btn"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-zinc-300 bg-white hover:border-orange-500 text-xs font-bold uppercase tracking-wider"
          >
            <Plus className="h-3.5 w-3.5" /> Add Issue
          </button>
        )}
      </div>

      {adding && (
        <div
          className="flex items-center gap-2 mb-4 p-3 border border-orange-300 bg-orange-50 rounded-sm"
          data-testid="add-issue-row"
        >
          <Input
            data-testid="new-issue-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder='Issue label (e.g. "Compressor seal" or "Roof east drain")'
            className="rounded-sm h-9 flex-1"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddGroup();
              if (e.key === "Escape") {
                setAdding(false);
                setNewName("");
              }
            }}
          />
          <Button
            data-testid="confirm-new-issue"
            onClick={handleAddGroup}
            size="sm"
            className="rounded-sm h-9 bg-orange-500 hover:bg-orange-600 font-bold"
          >
            Add
          </Button>
          <button
            onClick={() => {
              setAdding(false);
              setNewName("");
            }}
            className="p-2 text-zinc-500 hover:text-zinc-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g) => {
          const list = photosFor(g);
          const before = list.filter((p) => p.kind !== "after");
          const after = list.filter((p) => p.kind === "after");
          const latestBefore = before[before.length - 1];
          const latestAfter = after[after.length - 1];
          const canCompare = !!latestBefore && !!latestAfter;

          return (
            <div
              key={g}
              data-testid={`group-${g}`}
              className="border border-zinc-200 rounded-sm bg-zinc-50/50 p-3"
            >
              <GroupHeader
                group={g}
                isMain={g === DEFAULT_GROUP}
                beforeCount={before.length}
                afterCount={after.length}
                canCompare={canCompare}
                canEdit={canEdit}
                woId={woId}
                aiEnabled={aiEnabled}
                caption={captions?.[g]?.text || null}
                onCompare={() =>
                  setCompare({
                    beforeUrl: latestBefore.url,
                    afterUrl: latestAfter.url,
                    label: g,
                  })
                }
                onChanged={onChanged}
                onLocalRemove={() =>
                  setExtraGroups((cur) => cur.filter((x) => x !== g))
                }
              />

              <div className="grid grid-cols-2 gap-3">
                <Column
                  title="Before"
                  group={g}
                  testid={`col-before-${g}`}
                  accent="border-zinc-300"
                  tagClass="bg-zinc-900 text-white"
                  photos={before}
                  onClickPhoto={(p, i) =>
                    setOpenIdx({ group: g, side: "before", index: i })
                  }
                  canEdit={canEdit}
                  uploading={uploading === `${g}__before`}
                  onUpload={(e) => onUpload(e, "before", g)}
                />
                <Column
                  title="After"
                  group={g}
                  testid={`col-after-${g}`}
                  accent="border-emerald-400"
                  tagClass="bg-emerald-600 text-white"
                  photos={after}
                  onClickPhoto={(p, i) =>
                    setOpenIdx({ group: g, side: "after", index: i })
                  }
                  canEdit={canEdit}
                  uploading={uploading === `${g}__after`}
                  onUpload={(e) => onUpload(e, "after", g)}
                />
              </div>
            </div>
          );
        })}
      </div>

      {compare && (
        <CompareModal
          beforeUrl={compare.beforeUrl}
          afterUrl={compare.afterUrl}
          label={compare.label}
          onClose={() => setCompare(null)}
        />
      )}

      {openIdx !== null && (() => {
        const list = photosFor(openIdx.group).filter(
          (p) => (openIdx.side === "before" ? p.kind !== "after" : p.kind === "after")
        );
        const p = list[openIdx.index];
        if (!p) return null;
        return (
          <Lightbox
            url={p.url}
            label={`${openIdx.group} · ${openIdx.side === "before" ? "Before" : "After"} · ${p.by || ""}`}
            onClose={() => setOpenIdx(null)}
            onPrev={
              openIdx.index > 0
                ? () => setOpenIdx({ ...openIdx, index: openIdx.index - 1 })
                : null
            }
            onNext={
              openIdx.index < list.length - 1
                ? () => setOpenIdx({ ...openIdx, index: openIdx.index + 1 })
                : null
            }
          />
        );
      })()}
    </div>
  );
}

const Column = ({
  title,
  group,
  testid,
  accent,
  tagClass,
  photos,
  onClickPhoto,
  canEdit,
  uploading,
  onUpload,
}) => {
  const inputRef = useRef(null);
  const slug = title.toLowerCase();
  const groupSlug = group.replace(/\s+/g, "-").toLowerCase();
  return (
    <div className={`border-2 border-dashed ${accent} rounded-sm p-3 bg-white`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span
          className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-[0.12em] ${tagClass}`}
        >
          {title}
        </span>
        <span className="font-mono text-[10px] text-zinc-500">{photos.length}</span>
      </div>
      {photos.length === 0 ? (
        <div className="aspect-video bg-zinc-50 border border-zinc-200 rounded-sm flex items-center justify-center text-xs text-zinc-400">
          No {title.toLowerCase()} photo
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {photos.map((p, i) => (
            <button
              key={p.url || i}
              data-testid={`photo-${groupSlug}-${slug}-${i}`}
              onClick={() => onClickPhoto(p, i)}
              className="aspect-square bg-zinc-100 border border-zinc-200 rounded-sm overflow-hidden hover:border-orange-500"
            >
              <img src={p.url} alt={`${title} ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {canEdit && (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`upload-${groupSlug}-${slug}`}
          className="mt-3 w-full h-9 inline-flex items-center justify-center gap-1.5 rounded-sm border border-zinc-300 bg-white hover:border-orange-500 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
        >
          <Upload className="h-3.5 w-3.5" />
          {uploading ? "Uploading…" : `Add ${title}`}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onUpload}
            disabled={uploading}
            className="hidden"
          />
        </button>
      )}
    </div>
  );
};

function GroupHeader({
  group,
  isMain,
  beforeCount,
  afterCount,
  canCompare,
  canEdit,
  woId,
  aiEnabled,
  caption,
  onCompare,
  onChanged,
  onLocalRemove,
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group);
  const [busy, setBusy] = useState(false);

  const isEmpty = beforeCount + afterCount === 0;

  const saveRename = async () => {
    const v = name.trim();
    if (!v || v === group) {
      setEditing(false);
      setName(group);
      return;
    }
    if (isEmpty) {
      onLocalRemove();
      onChanged?.({ renamedLocal: { from: group, to: v } });
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await renameGroup(woId, group, v);
      toast.success("Group renamed");
      onChanged?.({});
      setEditing(false);
    } catch {
      toast.error("Rename failed");
      setName(group);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete group "${group}" and all its photos?`)) return;
    if (isEmpty) {
      onLocalRemove();
      return;
    }
    setBusy(true);
    try {
      await deleteGroup(woId, group);
      toast.success("Group deleted");
      onChanged?.({});
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const handleCaption = async () => {
    setBusy(true);
    try {
      await generateGroupCaption(woId, group);
      toast.success("Caption generated");
      onChanged?.({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caption failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-1 mb-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {editing ? (
            <>
              <Input
                data-testid={`rename-input-${group}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveRename();
                  if (e.key === "Escape") {
                    setEditing(false);
                    setName(group);
                  }
                }}
                autoFocus
                className="rounded-sm h-7 w-44"
              />
              <Button
                data-testid={`rename-save-${group}`}
                size="sm"
                onClick={saveRename}
                disabled={busy}
                className="h-7 px-2 rounded-sm bg-orange-500 hover:bg-orange-600 font-bold text-[10px]"
              >
                Save
              </Button>
              <button
                onClick={() => {
                  setEditing(false);
                  setName(group);
                }}
                className="p-1 text-zinc-500 hover:text-zinc-900"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              <span
                data-testid={`group-title-${group}`}
                className="font-display font-bold text-base text-zinc-950 truncate"
              >
                {group}
              </span>
              <span className="font-mono text-[10px] text-zinc-500 shrink-0">
                · {beforeCount}↦{afterCount}
              </span>
            </>
          )}
        </div>
        {!editing && (
          <div className="flex items-center gap-1 shrink-0">
            {canCompare && (
              <button
                data-testid={`compare-${group}`}
                onClick={onCompare}
                className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-orange-400 bg-orange-50 hover:bg-orange-100 text-[10px] font-bold uppercase tracking-wider text-orange-800"
              >
                <ArrowLeftRight className="h-3 w-3" /> Compare
              </button>
            )}
            {canEdit && canCompare && aiEnabled && woId && (
              <button
                data-testid={`caption-${group}`}
                onClick={handleCaption}
                disabled={busy}
                className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-orange-300 bg-white hover:bg-orange-50 text-[10px] font-bold uppercase tracking-wider text-orange-700 disabled:opacity-50"
                title="Generate AI caption"
              >
                <Sparkles className="h-3 w-3" /> {busy ? "…" : "AI"}
              </button>
            )}
            {canEdit && (
              <>
                <button
                  data-testid={`rename-${group}`}
                  onClick={() => setEditing(true)}
                  className="p-1.5 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-600"
                  title="Rename"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                {!isMain && (
                  <button
                    data-testid={`delete-${group}`}
                    onClick={handleDelete}
                    disabled={busy}
                    className="p-1.5 rounded-sm border border-red-300 bg-white hover:bg-red-50 text-red-600 disabled:opacity-50"
                    title="Delete group"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {caption && (
        <div
          data-testid={`caption-text-${group}`}
          className="mt-2 px-2 py-1.5 bg-orange-50 border-l-2 border-orange-500 rounded-sm text-xs text-zinc-800 leading-relaxed"
        >
          <Sparkles className="h-3 w-3 inline mr-1 text-orange-600" />
          {caption}
        </div>
      )}
    </div>
  );
}

function Lightbox({ url, label, onClose, onPrev, onNext }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950/90 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="wo-lightbox"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 text-white/70 hover:text-white"
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>
      {onPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 text-white/70 hover:text-white"
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
      )}
      <div className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt="" className="max-h-[80vh] mx-auto object-contain" />
        <div className="mt-3 text-center text-xs font-mono text-white/70">{label}</div>
      </div>
      {onNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 text-white/70 hover:text-white"
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      )}
    </div>
  );
}

function CompareModal({ beforeUrl, afterUrl, label, onClose }) {
  const [pct, setPct] = useState(50);
  const [mode, setMode] = useState("slider");

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950/90 flex items-center justify-center p-4"
      data-testid="wo-compare-modal"
    >
      <div className="bg-white rounded-sm max-w-6xl w-full max-h-full overflow-auto">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <GitCompareArrows className="h-5 w-5 text-orange-500 shrink-0" />
            <div className="font-display font-bold text-lg truncate">
              Before / After · <span className="text-orange-600">{label}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setMode("slider")}
              data-testid="compare-mode-slider"
              className={`px-3 h-8 rounded-sm text-xs font-bold uppercase tracking-wider border ${
                mode === "slider"
                  ? "bg-orange-500 text-white border-orange-500"
                  : "border-zinc-300 text-zinc-700"
              }`}
            >
              Slider
            </button>
            <button
              onClick={() => setMode("side")}
              data-testid="compare-mode-side"
              className={`px-3 h-8 rounded-sm text-xs font-bold uppercase tracking-wider border ${
                mode === "side"
                  ? "bg-orange-500 text-white border-orange-500"
                  : "border-zinc-300 text-zinc-700"
              }`}
            >
              Side by side
            </button>
            <button
              onClick={onClose}
              data-testid="compare-close"
              className="ml-2 p-2 text-zinc-500 hover:text-zinc-900"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-5">
          {mode === "slider" ? (
            <SliderCompare beforeUrl={beforeUrl} afterUrl={afterUrl} pct={pct} setPct={setPct} />
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <Panel label="Before" url={beforeUrl} accent="bg-zinc-900 text-white" />
              <Panel label="After" url={afterUrl} accent="bg-emerald-600 text-white" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Panel = ({ label, url, accent }) => (
  <div className="border border-zinc-200 rounded-sm overflow-hidden">
    <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${accent}`}>
      {label}
    </div>
    <div className="bg-zinc-100">
      <img src={url} alt={label} className="w-full max-h-[60vh] object-contain" />
    </div>
  </div>
);

function SliderCompare({ beforeUrl, afterUrl, pct, setPct }) {
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromEvent = (clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPct(Math.max(0, Math.min(100, p)));
  };

  const onDown = (e) => {
    dragging.current = true;
    setFromEvent(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    setFromEvent(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onUp = () => {
    dragging.current = false;
  };

  return (
    <div
      ref={ref}
      data-testid="compare-slider"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
      className="relative w-full bg-zinc-100 border border-zinc-200 rounded-sm overflow-hidden select-none"
      style={{ maxHeight: "70vh" }}
    >
      <img src={afterUrl} alt="After" className="block w-full max-h-[70vh] object-contain" />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
      >
        <img src={beforeUrl} alt="Before" className="block w-full max-h-[70vh] object-contain" />
      </div>
      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-zinc-900 text-white">
        Before
      </span>
      <span className="absolute top-2 right-2 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white">
        After
      </span>
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-orange-500 pointer-events-none"
        style={{ left: `${pct}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-orange-500 border-4 border-white shadow-lg flex items-center justify-center cursor-ew-resize"
        style={{ left: `${pct}%` }}
      >
        <ArrowLeftRight className="h-4 w-4 text-white" />
      </div>
    </div>
  );
}
